import {
  createBaseThemeOptions,
  createUnifiedTheme,
  genPageTheme,
  shapes,
  UnifiedTheme,
} from '@backstage/theme';
import { primitiveColors, lightModeColors, darkModeColors } from './colorToken';

export const myLightTheme: UnifiedTheme = createUnifiedTheme({
  ...createBaseThemeOptions({
    palette: {
      type: 'light',
      mode: 'light',
      background: {
        default: lightModeColors.background.default,
        paper: lightModeColors.surface.default,
      },
      status: {
        ok: lightModeColors.functionalColors.success[1],
        warning: lightModeColors.functionalColors.warning.orange1,
        error: lightModeColors.functionalColors.error.default,
        running: lightModeColors.functionalColors.info[1],
        pending: lightModeColors.functionalColors.warning.yellow1,
        aborted: primitiveColors.gray[50],
      },
      bursts: {
        fontColor: primitiveColors.white,
        slackChannelText: primitiveColors.gray[20],
        backgroundColor: {
          default: primitiveColors.connectLightBlue,
        },
        gradient: {
          linear: `linear-gradient(-137deg, ${primitiveColors.connectLightBlue} 0%, ${primitiveColors.connectMediumBlue} 100%)`,
        },
      },
      primary: {
        main: lightModeColors.surface.main,
      },
      secondary: {
        main: primitiveColors.connectMediumBlue,
      },
      banner: {
        info: lightModeColors.functionalColors.info[1],
        error: lightModeColors.functionalColors.error.default,
        text: primitiveColors.white,
        link: primitiveColors.white,
        closeButtonColor: primitiveColors.white,
        warning: lightModeColors.functionalColors.warning.orange1,
      },
      border: lightModeColors.border.default,
      textContrast: lightModeColors.text.default,
      textVerySubtle: lightModeColors.text.subtle,
      textSubtle: lightModeColors.text.subtle,
      highlight: lightModeColors.functionalColors.warning.yellow1,
      errorBackground: lightModeColors.functionalColors.error.subtle,
      warningBackground: primitiveColors.orange[10],
      infoBackground: primitiveColors.blue[10],
      errorText: lightModeColors.functionalColors.error.default,
      infoText: lightModeColors.functionalColors.info[1],
      warningText: lightModeColors.functionalColors.warning.orange1,
      linkHover: primitiveColors.connectMediumBlue,
      link: lightModeColors.surface.main,
      gold: primitiveColors.yellow[60],
      navigation: {
        background: lightModeColors.specific.globalNavigationBackground,
        indicator: lightModeColors.specific.globalNavigationSelector,
        color: lightModeColors.specific.globalNavigationSurfaceDefault,
        selectedColor: lightModeColors.specific.globalNavigationSurfaceDefault,
        navItem: {
          hoverBackground:
            lightModeColors.specific.globalNavigationSurfaceDefaultHovered,
        },
        submenu: {
          background: primitiveColors.white,
        },
      },
      pinSidebarButton: {
        icon: lightModeColors.surface.main,
        background: primitiveColors.white,
      },
      tabbar: {
        indicator: lightModeColors.surface.main,
      },
    },
  }),
  pageTheme: {
    home: genPageTheme({
      colors: [
        primitiveColors.connectMediumBlue,
        primitiveColors.connectDarkBlue,
      ],
      shape: shapes.wave,
    }),
    documentation: genPageTheme({
      colors: [
        primitiveColors.connectMediumBlue,
        primitiveColors.connectDarkBlue,
      ],
      shape: shapes.wave,
    }),
    tool: genPageTheme({
      colors: [
        primitiveColors.connectMediumBlue,
        primitiveColors.connectDarkBlue,
      ],
      shape: shapes.round,
    }),
    service: genPageTheme({
      colors: [
        primitiveColors.connectMediumBlue,
        primitiveColors.connectDarkBlue,
      ],
      shape: shapes.wave,
    }),
    website: genPageTheme({
      colors: [
        primitiveColors.connectMediumBlue,
        primitiveColors.connectDarkBlue,
      ],
      shape: shapes.wave,
    }),
    library: genPageTheme({
      colors: [
        primitiveColors.connectMediumBlue,
        primitiveColors.connectDarkBlue,
      ],
      shape: shapes.wave,
    }),
    other: genPageTheme({
      colors: [
        primitiveColors.connectMediumBlue,
        primitiveColors.connectDarkBlue,
      ],
      shape: shapes.wave,
    }),
    app: genPageTheme({
      colors: [
        primitiveColors.connectMediumBlue,
        primitiveColors.connectDarkBlue,
      ],
      shape: shapes.wave,
    }),
    apis: genPageTheme({
      colors: [
        primitiveColors.connectMediumBlue,
        primitiveColors.connectDarkBlue,
      ],
      shape: shapes.wave,
    }),
  },
});

export const myDarkTheme: UnifiedTheme = createUnifiedTheme({
  ...createBaseThemeOptions({
    palette: {
      type: 'dark',
      mode: 'dark',
      background: {
        default: darkModeColors.background.default,
        paper: darkModeColors.surface.default,
      },
      status: {
        ok: darkModeColors.functionalColors.success[1],
        warning: darkModeColors.functionalColors.warning.orange1,
        error: darkModeColors.functionalColors.error.default,
        running: darkModeColors.functionalColors.info[1],
        pending: darkModeColors.functionalColors.warning.yellow1,
        aborted: primitiveColors.gray[50],
      },
      bursts: {
        fontColor: primitiveColors.white,
        slackChannelText: primitiveColors.gray[20],
        backgroundColor: {
          default: primitiveColors.connectLightBlue,
        },
        gradient: {
          linear: `linear-gradient(-137deg, ${primitiveColors.connectLightBlue} 0%, ${primitiveColors.connectMediumBlue} 100%)`,
        },
      },
      primary: {
        main: darkModeColors.surface.main,
        dark: primitiveColors.connectMediumBlue,
      },
      secondary: {
        main: primitiveColors.connectMediumBlue,
      },
      banner: {
        info: darkModeColors.functionalColors.info[1],
        error: darkModeColors.functionalColors.error.default,
        text: primitiveColors.white,
        link: primitiveColors.white,
        closeButtonColor: primitiveColors.white,
        warning: darkModeColors.functionalColors.warning.orange1,
      },
      border: darkModeColors.border.default,
      textContrast: darkModeColors.text.default,
      textVerySubtle: darkModeColors.text.subtle,
      textSubtle: darkModeColors.text.subtle,
      highlight: darkModeColors.functionalColors.warning.yellow1,
      errorBackground: darkModeColors.functionalColors.error.subtle,
      warningBackground: primitiveColors.orange[100],
      infoBackground: primitiveColors.blue[100],
      errorText: darkModeColors.functionalColors.error.default,
      infoText: darkModeColors.functionalColors.info[1],
      warningText: darkModeColors.functionalColors.warning.orange2,
      linkHover: primitiveColors.connectMediumBlue,
      link: darkModeColors.surface.main,
      gold: primitiveColors.yellow[60],
      navigation: {
        background: darkModeColors.specific.globalNavigationBackground,
        indicator: darkModeColors.specific.globalNavigationSelector,
        color: primitiveColors.gray[40],
        selectedColor: darkModeColors.specific.globalNavigationSurfaceDefault,
        navItem: {
          hoverBackground:
            darkModeColors.specific.globalNavigationSurfaceDefaultHovered,
        },
        submenu: {
          background: primitiveColors.gray[95],
        },
      },
      pinSidebarButton: {
        icon: darkModeColors.surface.main,
        background: primitiveColors.gray[95],
      },
      tabbar: {
        indicator: darkModeColors.surface.main,
      },
    },
  }),
  pageTheme: {
    home: genPageTheme({
      colors: [
        primitiveColors.connectDarkBlue,
        primitiveColors.connectMediumBlue,
      ],
      shape: shapes.wave,
    }),
    documentation: genPageTheme({
      colors: [
        primitiveColors.connectDarkBlue,
        primitiveColors.connectMediumBlue,
      ],
      shape: shapes.wave,
    }),
    tool: genPageTheme({
      colors: [
        primitiveColors.connectDarkBlue,
        primitiveColors.connectMediumBlue,
      ],
      shape: shapes.round,
    }),
    service: genPageTheme({
      colors: [
        primitiveColors.connectDarkBlue,
        primitiveColors.connectMediumBlue,
      ],
      shape: shapes.wave,
    }),
    website: genPageTheme({
      colors: [
        primitiveColors.connectDarkBlue,
        primitiveColors.connectMediumBlue,
      ],
      shape: shapes.wave,
    }),
    library: genPageTheme({
      colors: [
        primitiveColors.connectDarkBlue,
        primitiveColors.connectMediumBlue,
      ],
      shape: shapes.wave,
    }),
    other: genPageTheme({
      colors: [
        primitiveColors.connectDarkBlue,
        primitiveColors.connectMediumBlue,
      ],
      shape: shapes.wave,
    }),
    app: genPageTheme({
      colors: [
        primitiveColors.connectDarkBlue,
        primitiveColors.connectMediumBlue,
      ],
      shape: shapes.wave,
    }),
    apis: genPageTheme({
      colors: [
        primitiveColors.connectDarkBlue,
        primitiveColors.connectMediumBlue,
      ],
      shape: shapes.wave,
    }),
  },
});
